import React, { useEffect } from "react";
import { createPortal } from "react-dom"; // Import createPortal
import { useTranslation } from "react-i18next";
import type { HeaderOffcanvasTab } from "../../headerUiSlice";

interface OffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  activeTab: HeaderOffcanvasTab;
  onTabClick: (tab: HeaderOffcanvasTab) => void;
  children: React.ReactNode;
}

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="offcanvas-close-icon"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const Offcanvas: React.FC<OffcanvasProps> = ({
  isOpen,
  onClose,
  activeTab,
  onTabClick,
  children,
}) => {
  const { t } = useTranslation();
  const portalRoot = document.getElementById("offcanvas-portal-root"); // Get portal root

  // Effect to handle Escape key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Effect to add/remove body class for preventing background scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("header-offcanvas-open");
    } else {
      document.body.classList.remove("header-offcanvas-open");
    }
    // Cleanup function
    return () => {
      document.body.classList.remove("header-offcanvas-open");
    };
  }, [isOpen]);

  // Render nothing only if the portal root doesn't exist
  if (!portalRoot) {
    console.error("Offcanvas portal root element not found.");
    return null;
  }

  // Use createPortal to render into the target element.
  // The component stays mounted, visibility/animation controlled by CSS via isOpen prop.
  return createPortal(
    <>
      {/* Overlay */}
      <div
        className={`offcanvas-overlay ${isOpen ? "is-open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Offcanvas Panel */}
      <div
        className={`offcanvas-panel ${isOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="offcanvas-title"
      >
        <div className="offcanvas-header">
          {/* Tabs */}
          <div className="offcanvas-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={activeTab === "search"}
              aria-controls="offcanvas-search-panel"
              className={`offcanvas-tab ${
                activeTab === "search" ? "is-active" : ""
              }`}
              onClick={() => onTabClick("search")}
            >
              {t("headerActions.search")}
            </button>
            <button
              role="tab"
              aria-selected={activeTab === "cart"}
              aria-controls="offcanvas-cart-panel"
              className={`offcanvas-tab ${
                activeTab === "cart" ? "is-active" : ""
              }`}
              onClick={() => onTabClick("cart")}
            >
              {t("headerActions.cart")}
            </button>
          </div>
          {/* Close Button */}
          <button
            className="offcanvas-close-button"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Content Area */}
        <div className="offcanvas-content">{children}</div>
      </div>
    </>,
    portalRoot // Target the portal root element
  );
};

export default Offcanvas;
